import { getApiUrl } from '../config/api';
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
  useRef,
} from 'react';
import { Client } from '../types';
import { useAuth } from '../hooks/useAuth';
import { readAuthToken } from '@/utils/authToken';

interface ClientContextType {
  selectedClient: Client | null;
  setSelectedClient: (client: Client | null) => void;
  availableClients: Client[];
  isLoading: boolean;
  error: string | null;
  refreshClients: () => Promise<void>;
}

const ClientContext = createContext<ClientContextType | undefined>(undefined);
const CLIENT_STORAGE_KEYS = {
  full: 'selectedClient',
  id: 'selectedClientId',
  name: 'selectedClientName',
  code: 'selectedClientCode',
} as const;


// Validate that a stored client ID is a real UUID (not a legacy slug like 'client-altru')
const isValidUUID = (id: string) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);

const normalizeToCanonical = (
  client: Partial<Client> | null | undefined
): Client | null => {
  if (!client || !client.id || !client.name) {
    return null;
  }

  return {
    id: client.id,
    name: client.name,
    code: client.code || undefined,
    domain: client.domain,
    isActive: client.isActive ?? true,
    createdAt: client.createdAt || '',
    updatedAt: client.updatedAt || '',
  } as Client;
};

const clearStoredClient = () => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    Object.values(CLIENT_STORAGE_KEYS).forEach((key) => {
      window.localStorage.removeItem(key);
    });
  } catch (error) {
    console.warn('Failed to clear stored client context', error);
  }
};

const writeCanonicalClientToStorage = (client: Client) => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(
      CLIENT_STORAGE_KEYS.full,
      JSON.stringify(client)
    );
    window.localStorage.setItem(CLIENT_STORAGE_KEYS.id, client.id);
    window.localStorage.setItem(CLIENT_STORAGE_KEYS.name, client.name);

    if (client.code) {
      window.localStorage.setItem(CLIENT_STORAGE_KEYS.code, client.code);
    } else {
      window.localStorage.removeItem(CLIENT_STORAGE_KEYS.code);
    }
  } catch (error) {
    console.warn('Failed to persist selected client context', error);
  }
};

const ensureCanonicalClientFromStorage = (): Client | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const cached = window.localStorage.getItem(CLIENT_STORAGE_KEYS.full);
    if (cached) {
      const parsed = JSON.parse(cached) as Partial<Client>;
      // Reject stale slug-style IDs (e.g., 'client-altru') — only accept UUIDs
      if (parsed.id && !isValidUUID(parsed.id)) {
        clearStoredClient();
        return null;
      }
      const normalized = normalizeToCanonical(parsed);
      if (normalized) {
        writeCanonicalClientToStorage(normalized);
        return normalized;
      }
    }

    const storedId = window.localStorage.getItem(CLIENT_STORAGE_KEYS.id);
    // Reject stale slug-style IDs
    if (storedId && !isValidUUID(storedId)) {
      clearStoredClient();
      return null;
    }

    const fallback = normalizeToCanonical({
      id: storedId ?? undefined,
      code: window.localStorage.getItem(CLIENT_STORAGE_KEYS.code) ?? undefined,
      name: window.localStorage.getItem(CLIENT_STORAGE_KEYS.name) ?? undefined,
      isActive: true,
    });

    if (fallback) {
      writeCanonicalClientToStorage(fallback);
      return fallback;
    }
  } catch (error) {
    console.warn('Failed to normalize stored client context', error);
  }

  clearStoredClient();
  return null;
};

export const useClient = () => {
  const context = useContext(ClientContext);
  if (!context) {
    throw new Error('useClient must be used within a ClientProvider');
  }
  return context;
};

interface ClientProviderProps {
  children: ReactNode;
}

const hydrateStoredClient = (): Client | null => {
  return ensureCanonicalClientFromStorage();
};

export const ClientProvider: React.FC<ClientProviderProps> = ({ children }) => {
  const [selectedClient, setSelectedClientState] = useState<Client | null>(() =>
    hydrateStoredClient()
  );
  const [availableClients, setAvailableClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated } = useAuth();
  const selectedClientRef = useRef<Client | null>(selectedClient);

  useEffect(() => {
    selectedClientRef.current = selectedClient;
  }, [selectedClient]);

  const handleSetSelectedClient = useCallback(async (client: Client | null) => {
    const canonicalClient = normalizeToCanonical(client);
    setSelectedClientState(canonicalClient);
    selectedClientRef.current = canonicalClient;

    if (canonicalClient) {
      writeCanonicalClientToStorage(canonicalClient);

      // Nothing to tell the server when there is no sign-in: the request could only be
      // refused, and each refusal is written to the audit trail as a blocked request.
      const token = readAuthToken();
      if (!token) return;

      try {
        // Send the bearer token like every other API call. With the session cookie alone
        // (15-minute rolling), a signed-in user switching clients after 15 idle minutes was
        // refused and audited as "unauthorized access" — measured 2026-09-30, the largest
        // single source of those rows in production.
        const response = await fetch(
          getApiUrl(`/api/clients/${canonicalClient.id}/select`),
          {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          console.warn(`Failed to select client on backend: ${response.status}`);
        }
      } catch (err) {
        console.error('Error selecting client on backend:', err);
      }
    } else {
      clearStoredClient();
    }
  }, []);

  const fetchClients = useCallback(async () => {
    if (!isAuthenticated) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Import apiService at top of file to use axios interceptor with JWT token
      const { default: apiService } = await import('../services/api');
      const response = await apiService.get<Client[]>('/clients');
      const data = response.data;
      const rawClients: Client[] = Array.isArray(data?.data) ? data.data : [];

      const normalizedClients = rawClients
        .map((client) => normalizeToCanonical(client))
        .filter((client): client is Client => client !== null);

      // Use only actual clients from API - don't fall back to hardcoded canonical clients
      // If no clients exist, show empty state instead of phantom clients
      const workingSet = normalizedClients;

      setAvailableClients(workingSet);

      // If no clients available, clear any cached selection
      if (workingSet.length === 0) {
        clearStoredClient();
        setSelectedClientState(null);
        selectedClientRef.current = null;
        setIsLoading(false);
        return;
      }

      const savedClientId = localStorage.getItem('selectedClientId');
      const savedClientCode = localStorage.getItem('selectedClientCode');
      const savedClientName = localStorage.getItem('selectedClientName');

      let candidate =
        workingSet.find((client) => client.id === savedClientId) ??
        workingSet.find((client) => client.code === savedClientCode) ??
        (savedClientName
          ? workingSet.find(
              (client) =>
                client.name.toLowerCase() === savedClientName.toLowerCase()
            )
          : undefined);

      if (!candidate && !selectedClientRef.current && workingSet.length > 0) {
        candidate = workingSet[0];
      }

      if (
        candidate &&
        (!selectedClientRef.current ||
          selectedClientRef.current.id !== candidate.id)
      ) {
        await handleSetSelectedClient(candidate);
      }
    } catch (err) {
      console.error('Error fetching clients:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch clients');

      // Don't fallback to hardcoded clients on error - show error state instead
      // Clear any stale cached selection that might reference non-existent clients
      setAvailableClients([]);
      clearStoredClient();
      setSelectedClientState(null);
      selectedClientRef.current = null;
    } finally {
      setIsLoading(false);
    }
  }, [handleSetSelectedClient, isAuthenticated]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const refreshClients = useCallback(async () => {
    await fetchClients();
  }, [fetchClients]);

  return (
    <ClientContext.Provider
      value={{
        selectedClient,
        setSelectedClient: handleSetSelectedClient,
        availableClients,
        isLoading,
        error,
        refreshClients,
      }}
    >
      {children}
    </ClientContext.Provider>
  );
};
